import { useState } from "react";
import presenceDefault from "@/assets/presence-default.webp";
import presenceCdmx from "@/assets/presence-cdmx.webp";
import presenceLima from "@/assets/presence-lima.webp";
import presenceUsa from "@/assets/presence-usa.webp";

interface Office {
  city: string;
  country: string;
  note: string;
}

const cityImages = [presenceUsa, presenceCdmx, presenceLima];

const GlobalPresenceGrid = ({ offices }: { offices: Office[] }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div className="relative grid gap-0 md:grid-cols-3 max-w-4xl mx-auto rounded-xl overflow-hidden">
      {/* Default background — one continuous image behind all columns */}
      <img
        src={presenceDefault}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {offices.map((office, i) => (
        <div
          key={office.city}
          className="relative h-64 md:h-72 cursor-pointer overflow-hidden group"
          onMouseEnter={() => setHovered(i)}
          onMouseLeave={() => setHovered(null)}
        >

          {/* City image — visible on hover */}
          <img
            src={cityImages[i]}
            alt={office.city}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-all duration-500"
            style={{
              opacity: hovered === i ? 1 : 0,
              transform: hovered === i ? "scale(1.05)" : "scale(1)",
              filter: "saturate(0.3)",
            }}
          />

          {/* Dark overlay on city image for brand consistency */}
          <div
            className="absolute inset-0 bg-[hsl(226,55%,24%)] transition-opacity duration-500"
            style={{ opacity: hovered === i ? 0.45 : 0 }}
          />

          {/* Always-present subtle overlay for text legibility on default image */}
          <div className="absolute inset-0 bg-black/30" />

          {/* Vertical divider */}
          {i < 2 && (
            <div className="hidden md:block absolute right-0 top-1/4 bottom-1/4 w-px bg-white/20" />
          )}

          {/* Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 transition-all duration-500">
            <h3
              className="font-heading text-xl mb-1 transition-all duration-500"
              style={{
                color: hovered === i ? "white" : "rgba(255,255,255,0.9)",
                transform: hovered === i ? "translateY(-4px)" : "translateY(0)",
              }}
            >
              {office.city}
            </h3>
            <p
              className="text-sm transition-all duration-500"
              style={{ color: hovered === i ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.6)" }}
            >
              {office.country}
            </p>
            <p
              className="text-xs mt-2 italic transition-all duration-500"
              style={{ color: hovered === i ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.4)" }}
            >
              {office.note}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GlobalPresenceGrid;
